import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowRight } from "lucide-react";
import BrandLogo from "./BrandLogo.jsx";
import { MaskedLine, scrollToId, EASE } from "./Reveal.jsx";
import { useSiteContent, mediaUrl } from "@/content/ContentContext.jsx";

const Hero = () => {
  const { content } = useSiteContent();
  const c = content.hero;
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const badgeY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section ref={ref} className="relative min-h-screen overflow-hidden bg-white" data-testid="hero-section">
      <div className="pointer-events-none absolute -right-40 top-24 h-[540px] w-[540px] rounded-full bg-brand-tint2 blur-3xl" />
      <div className="pointer-events-none absolute -left-52 bottom-0 h-[420px] w-[420px] rounded-full bg-brand-tint blur-3xl" />

      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-4 pb-24 pt-32 sm:px-8 lg:grid-cols-12 lg:gap-8 lg:px-10 lg:pb-16 lg:pt-40">
        <div className="relative z-10 lg:col-span-7">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="flex flex-wrap items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.24em] text-brand-light"
            data-testid="hero-eyebrow"
          >
            <span className="h-px w-10 bg-brand-light" />
            {c.eyebrow}
          </motion.p>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.35 }}
            lang="ta"
            className="mt-6 font-tamil text-lg text-brand sm:text-xl"
            data-testid="hero-tamil-name"
          >
            {c.tamil}
          </motion.p>

          <h1 className="mt-4 font-display text-[2.6rem] font-medium leading-[1.06] tracking-tight text-brand sm:text-6xl lg:text-[4.6rem]">
            <MaskedLine delay={0.45}>{c.titleLine1}</MaskedLine>
            <MaskedLine delay={0.58}>
              <span>
                <em className="font-light italic text-brand-light">{c.titleAccent}</em>
                {c.titleRest}
              </span>
            </MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.95, ease: EASE }}
            className="mt-8 max-w-xl text-base leading-relaxed text-zinc-600 sm:text-lg"
            data-testid="hero-subline"
          >
            {c.subline}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.1, ease: EASE }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              onClick={() => scrollToId("booking")}
              className="group flex items-center gap-3 rounded-full bg-brand px-8 py-4 text-sm font-semibold uppercase tracking-[0.14em] text-white transition-all duration-300 hover:bg-brand-deep hover:shadow-[0_14px_34px_rgba(13,91,49,0.35)]"
              data-testid="hero-cta-book"
            >
              Book Your Stay
              <ArrowRight size={17} className="transition-transform duration-300 group-hover:translate-x-1" />
            </button>
            <button
              onClick={() => scrollToId("about")}
              className="rounded-full border border-brand/25 px-8 py-4 text-sm font-semibold uppercase tracking-[0.14em] text-brand transition-all duration-300 hover:border-brand hover:bg-brand-tint"
              data-testid="hero-cta-explore"
            >
              Explore the Estate
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.5, duration: 1 }}
            className="mt-16 hidden items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.22em] text-zinc-400 lg:flex"
            data-testid="hero-scroll-cue"
          >
            <ArrowDown size={14} className="animate-bounce" />
            Scroll to wander
          </motion.div>
        </div>

        <div className="relative lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, scale: 0.94, y: 30 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 1.1, delay: 0.7, ease: EASE }}
            className="relative mx-auto w-full max-w-[420px]"
          >
            <div className="absolute -left-4 -top-4 h-full w-full rounded-[999px_999px_28px_28px] border border-brand/20 sm:-left-6 sm:-top-6" />
            <div className="arch-frame relative overflow-hidden shadow-[0_30px_80px_rgba(7,58,30,0.22)]">
              <motion.img
                src={mediaUrl(c.image)}
                alt="Lush green farmhouse estate at Angukumaran Farmhouse"
                style={{ y: imgY, scale: 1.16 }}
                className="h-[420px] w-full object-cover sm:h-[540px]"
                data-testid="hero-image"
              />
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-brand-dark/35 via-transparent to-transparent" />
            </div>

            <motion.div
              style={{ y: badgeY }}
              className="absolute -bottom-8 -left-6 flex items-center gap-4 rounded-2xl border hairline bg-white/95 p-4 pr-6 shadow-[0_18px_50px_rgba(7,58,30,0.16)] backdrop-blur sm:-left-12"
              data-testid="hero-badge"
            >
              <span className="flex items-center justify-center">
                <BrandLogo size={48} rounded="rounded-xl" fallbackBg="bg-brand" testid="hero-badge-logo" />
              </span>
              <span className="leading-tight">
                <span className="block font-display text-sm font-semibold text-brand">Angukumaran Farmhouse</span>
                <span lang="ta" className="block font-tamil text-xs text-brand-light">
                  {c.tamil}
                </span>
              </span>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
